if (!customElements.get('dressthing-product-form')) {
  customElements.define(
    'dressthing-product-form',
    class DressthingProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.productInfo = this.closest('product-info');
        this.picker = this.productInfo?.querySelector('[data-dressthing-v2-case-picker]');
        this.configurationNode = this.picker?.querySelector('[data-dt-v2-configuration]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.externalDisabled = false;
        this.externalDisabledText = '';
        this.loading = false;

        try {
          this.configuration = JSON.parse(this.configurationNode?.textContent || '{}');
        } catch (error) {
          console.error('DRESSTHING v2 configuration could not be parsed.', error);
          this.configuration = { valid: false };
        }

        if (!this.form || !this.submitButton || !this.picker) return;

        if (this.variantIdInput) this.variantIdInput.disabled = false;
        if (this.cart?.matches('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.applyUrlSelection();

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.picker.addEventListener('change', this.onSelectionChange.bind(this));
        this.variantIdInput?.addEventListener('change', this.onSelectionChange.bind(this));
        this.quantityInput?.addEventListener('change', this.onSelectionChange.bind(this));
        this.updateState();
      }

      onSelectionChange() {
        this.handleErrorMessage();
        this.updateState();
        this.syncUrlSelection();
      }

      applyUrlSelection() {
        const url = new URL(window.location.href);
        const requestedCase = url.searchParams.get('case');
        if (!['3d', 'acrylic', 'none'].includes(requestedCase)) return;

        const input = this.picker?.querySelector(`input[type="radio"][value="${requestedCase}"]`);
        if (input && !input.disabled) input.checked = true;
      }

      syncUrlSelection() {
        const state = this.getCurrentState();
        if (!state.mainVariant || !state.caseType) return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', state.mainVariant.id);
        url.searchParams.set('case', state.caseType);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      getSelectedMainVariant() {
        const variantId = Number(this.variantIdInput?.value);
        return this.configuration.mainVariants?.find((variant) => Number(variant.id) === variantId) || null;
      }

      getSelectedStyle(mainVariant) {
        if (!mainVariant) return null;

        const optionValues = Array.isArray(mainVariant.options) ? mainVariant.options : [mainVariant.title];
        return (
          Object.entries(this.configuration.styleValues || {}).find(([, configuredValue]) =>
            optionValues.some((value) => String(value).trim() === String(configuredValue).trim())
          )?.[0] || null
        );
      }

      getSelectedCaseType() {
        return this.picker.querySelector('input[type="radio"]:checked')?.value || this.configuration.defaultCase;
      }

      getCaseVariant(style, caseType) {
        if (caseType === 'none') return null;
        if (caseType === 'acrylic') return this.configuration.caseVariants?.acrylic || null;
        if (caseType === '3d') {
          return style === 'twins'
            ? this.configuration.caseVariants?.twins3d || null
            : this.configuration.caseVariants?.soloFold3d || null;
        }
        return null;
      }

      getQuantity() {
        const rawQuantity = Number(this.quantityInput?.value || 1);
        return Number.isInteger(rawQuantity) && rawQuantity > 0 ? rawQuantity : 0;
      }

      getCurrentState() {
        const mainVariant = this.getSelectedMainVariant();
        const style = this.getSelectedStyle(mainVariant);
        const caseType = this.getSelectedCaseType();
        const caseVariant = this.getCaseVariant(style, caseType);
        const quantity = this.getQuantity();
        const requiresCaseVariant = caseType !== 'none';
        const configurationValid = this.configuration.valid === true;
        const mainAvailable = mainVariant?.available === true;
        const caseAvailable = !requiresCaseVariant || caseVariant?.available === true;
        const valid =
          configurationValid &&
          Boolean(mainVariant) &&
          Boolean(style) &&
          quantity > 0 &&
          (!requiresCaseVariant || Boolean(caseVariant));

        return {
          mainVariant,
          style,
          caseType,
          caseVariant,
          quantity,
          valid,
          available: mainAvailable && caseAvailable,
        };
      }

      updateState() {
        if (!this.picker || !this.submitButton) return;

        const state = this.getCurrentState();
        this.updateCaseOptions(state);
        this.updateSummary(state);
        this.syncAcceleratedCheckoutFields(state);

        const disabled = this.loading || this.externalDisabled || !state.valid || !state.available;
        this.submitButton.toggleAttribute('disabled', disabled);
        this.submitButton.setAttribute('aria-disabled', String(disabled));

        if (!this.submitButtonText) return;
        if (!this.configuration.valid) {
          this.submitButtonText.textContent = '設定を確認してください';
        } else if (!state.valid) {
          this.submitButtonText.textContent = window.variantStrings?.unavailable || '選択できません';
        } else if (!state.available) {
          this.submitButtonText.textContent = window.variantStrings?.soldOut || '売り切れ';
        } else if (this.externalDisabled && this.externalDisabledText) {
          this.submitButtonText.textContent = this.externalDisabledText;
        } else {
          this.submitButtonText.textContent = window.variantStrings?.addToCart || 'カートに追加';
        }
      }

      updateCaseOptions(state) {
        const variants = this.configuration.caseVariants || {};
        const threeDimensionalVariant = state.style === 'twins' ? variants.twins3d : variants.soloFold3d;
        const acrylicVariant = variants.acrylic;

        this.setCaseOptionState('3d', threeDimensionalVariant, state);
        this.setCaseOptionState('acrylic', acrylicVariant, state);

        this.picker.querySelectorAll('.dt-v2-case-option').forEach((label) => {
          const input = label.querySelector('input[type="radio"]');
          label.classList.toggle('is-selected', input?.checked === true);
        });
      }

      setCaseOptionState(caseType, variant, state) {
        const input = this.picker.querySelector(`input[value="${caseType}"]`);
        const price = this.picker.querySelector(`[data-dt-v2-option-price="${caseType}"]`);
        const availability = this.picker.querySelector(`[data-dt-v2-case-availability="${caseType}"]`);
        const configured = Boolean(variant) && Boolean(state.style) && this.configuration.valid === true;
        const available = configured && variant.available === true;

        if (input) {
          input.disabled = !configured;
          input.closest('.dt-v2-case-option')?.classList.toggle('is-sold-out', configured && !available);
        }
        if (price) price.textContent = configured ? `+${this.formatMoney(variant.price)}` : '—';
        if (availability) {
          availability.textContent = configured && !available ? window.variantStrings?.soldOut || '売り切れ' : '';
        }
      }

      updateSummary(state) {
        const totalPriceNodes = this.productInfo?.querySelectorAll('[data-dt-v2-total-price]') || [];
        const detailNodes = this.productInfo?.querySelectorAll('[data-dt-v2-summary-detail]') || [];
        const styleSummaryNameNodes = this.productInfo?.querySelectorAll('[data-dt-v2-style-summary-name]') || [];
        const styleSummaryPriceNodes = this.productInfo?.querySelectorAll('[data-dt-v2-style-summary-price]') || [];
        const caseSummaryNameNodes = this.productInfo?.querySelectorAll('[data-dt-v2-case-summary-name]') || [];
        const caseSummaryPriceNodes = this.productInfo?.querySelectorAll('[data-dt-v2-case-summary-price]') || [];
        const soldOutBadges = this.productInfo?.querySelectorAll('[data-dt-v2-total-sold-out]') || [];
        const statusNode = this.picker.querySelector('[data-dt-v2-status]');
        const selectedStyleInput = this.productInfo?.querySelector(
          `[data-dtv2-style-input][value="${state.mainVariant?.id || ''}"]`
        );
        const styleName = selectedStyleInput?.dataset.styleName || state.mainVariant?.title || '';
        const styleDetail = selectedStyleInput?.dataset.styleDetail || '';
        const caseLabel = this.picker
          .querySelector(`input[value="${state.caseType}"]`)
          ?.closest('label')
          ?.querySelector('.dt-v2-case-option__title')
          ?.textContent.trim();
        const total = state.valid
          ? this.formatMoney(Number(state.mainVariant?.price || 0) + Number(state.caseVariant?.price || 0))
          : '—';
        const detail = state.valid ? `${styleName} / ${caseLabel || ''}` : '';

        totalPriceNodes.forEach((node) => {
          node.textContent = total;
        });
        detailNodes.forEach((node) => {
          node.textContent = detail;
        });
        styleSummaryNameNodes.forEach((node) => {
          node.textContent = state.mainVariant ? `${styleName}${styleDetail ? ` — ${styleDetail}` : ''}` : '—';
        });
        styleSummaryPriceNodes.forEach((node) => {
          node.textContent = state.mainVariant ? this.formatMoney(state.mainVariant.price) : '—';
        });
        caseSummaryNameNodes.forEach((node) => {
          node.textContent = caseLabel || '—';
        });
        caseSummaryPriceNodes.forEach((node) => {
          if (!state.valid && state.caseType !== 'none') {
            node.textContent = '—';
          } else {
            node.textContent = state.caseType === 'none' ? this.formatMoney(0) : `+${this.formatMoney(state.caseVariant?.price || 0)}`;
          }
        });
        soldOutBadges.forEach((badge) => {
          badge.hidden = !state.valid || state.available;
        });

        this.productInfo?.querySelector('[data-dtv2-selector-type="style"]')?.classList.toggle(
          'is-sold-out',
          Boolean(state.mainVariant) && state.mainVariant.available !== true
        );
        this.productInfo?.querySelector('[data-dtv2-selector-type="case"]')?.classList.toggle(
          'is-sold-out',
          state.caseType !== 'none' && Boolean(state.caseVariant) && state.caseVariant.available !== true
        );

        if (!statusNode) return;
        let message = '';
        if (!this.configuration.valid) {
          message = 'Case商品の対応設定が未完了です。';
        } else if (!state.style) {
          message = '選択中の本体バリアントをSolo／Fold／Twinsへ対応付けできません。';
        } else if (state.caseType !== 'none' && !state.caseVariant) {
          message = '選択したCaseの対応バリアントが見つかりません。';
        } else if (!state.available) {
          message = '選択中の構成には売り切れの商品が含まれています。';
        }
        statusNode.textContent = message;
        statusNode.hidden = message === '';
      }

      syncAcceleratedCheckoutFields(state) {
        const idInput = this.form.querySelector('[data-dt-v2-additional-id]');
        const quantityInput = this.form.querySelector('[data-dt-v2-additional-quantity]');
        const hasAdditionalItem = state.valid && state.caseType !== 'none' && Boolean(state.caseVariant);

        if (idInput) {
          idInput.value = hasAdditionalItem ? state.caseVariant.id : '';
          idInput.disabled = !hasAdditionalItem;
        }
        if (quantityInput) {
          quantityInput.value = state.quantity || 1;
          quantityInput.disabled = !hasAdditionalItem;
        }
      }

      formatMoney(cents) {
        const currency = this.configuration.currency || window.Shopify?.currency?.active || 'JPY';
        const locale = this.configuration.locale || document.documentElement.lang || 'ja';
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
          }).format(Number(cents || 0) / 100);
        } catch (error) {
          return `${Number(cents || 0) / 100} ${currency}`;
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        if (this.loading) return;

        const state = this.getCurrentState();
        if (!state.valid || !state.available) {
          this.updateState();
          this.handleErrorMessage('選択内容と在庫を確認してください。');
          return;
        }

        if (this.quantityInput && !this.quantityInput.checkValidity()) {
          this.quantityInput.reportValidity();
          return;
        }

        this.handleErrorMessage();
        this.setLoading(true);

        const items = [{ id: state.mainVariant.id, quantity: state.quantity }];
        if (state.caseVariant) items.push({ id: state.caseVariant.id, quantity: state.quantity });

        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        try {
          const request = await fetch(routes.cart_add_url, config);
          const response = await request.json();

          if (!request.ok || response.status) {
            const message = response.description || response.message || window.cartStrings?.error || 'カートへ追加できませんでした。';
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'dressthing-product-form',
              productVariantId: state.mainVariant.id,
              errors: response.errors || message,
              message,
            });
            this.handleErrorMessage(message);
            return;
          }

          if (this.cart && !(await this.ensureCartSections(response, body.sections || []))) {
            // The products have already been added successfully. If Shopify could not
            // bundle the drawer sections into the add response, send the customer to
            // the full cart instead of showing an error that can cause a duplicate add.
            window.location.assign(window.routes?.cart_url || '/cart');
            return;
          }

          // source: 'cart-items'（実際の発生源はこのフォーム）は意図的。Dawn標準のCartItems
          // （cart.js）はcartUpdateを受けてsourceが'cart-items'以外だと`onCartUpdate()`で
          // ドロワーを独自に再フェッチ・置換する。直後に呼ぶthis.cart.renderContents(response)と
          // 同じDOM領域を競合して書き換え、非決定的にエラーになっていたため、このフォーム発の
          // cartUpdateではCartItems側の重複更新を抑止する（renderContentsだけで表示は完結する）。
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            productVariantId: state.mainVariant.id,
            cartData: response,
          });

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (this.cart.matches('cart-notification') && Array.isArray(response.items) && response.items[0]) {
            response.id = response.items[0].id;
            response.key = response.items[0].key;
          }
          this.cart.renderContents(response);
        } catch (error) {
          console.error(error);
          this.handleErrorMessage(window.cartStrings?.error || '通信に失敗しました。時間をおいて再度お試しください。');
        } finally {
          this.setLoading(false);
        }
      }

      async ensureCartSections(response, sectionIds) {
        if (!Array.isArray(sectionIds) || sectionIds.length === 0) return true;

        const hasAllSections = () =>
          sectionIds.every((sectionId) => typeof response.sections?.[sectionId] === 'string');

        if (hasAllSections()) return true;

        try {
          const cartUrl = window.routes?.cart_url || '/cart';
          const separator = cartUrl.includes('?') ? '&' : '?';
          const params = new URLSearchParams({
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          });
          const sectionsRequest = await fetch(`${cartUrl}${separator}${params.toString()}`, {
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!sectionsRequest.ok) return false;

          const renderedSections = await sectionsRequest.json();
          response.sections = {
            ...(response.sections || {}),
            ...renderedSections,
          };
        } catch (error) {
          console.error('Cart sections could not be refreshed.', error);
          return false;
        }

        return hasAllSections();
      }

      setLoading(loading) {
        this.loading = loading;
        this.submitButton.classList.toggle('loading', loading);
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        this.updateState();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        this.externalDisabled = disable;
        this.externalDisabledText = text || '';
        this.updateState();
      }

      get quantityInput() {
        return this.productInfo?.querySelector('.quantity__input');
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}
